import { useContext, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { CircularProgress, Typography } from '@mui/material';
import { AuthContext } from '@/contexts/AuthContext';
import { useWorkflowStore } from '@/state/workflow.state';
import { FlexCenHorVer } from '@/UI/styles';
import { color } from '@/colors';
import { decodeFigmaEmbedToken } from '@/services/auth/figma-embed/figmaEmbedToken';
import { isInsideIframe } from '@/utils/auth.utils';
import { Recipe } from './Recipe';

export function EmbedWorkflow() {
  // Route is `/embed/flow/:recipeId` so Recipe's `useParams().recipeId` picks it up without change.
  const { recipeId } = useParams<{ recipeId: string }>();
  const { currentUser, accessToken } = useContext(AuthContext);
  const setWorkflowRole = useWorkflowStore((state) => state.setWorkflowRole);

  useEffect(() => {
    if (!accessToken) return;
    const payload = decodeFigmaEmbedToken(accessToken);
    const canEdit = payload?.permission === 'edit';
    setWorkflowRole(canEdit ? 'editor' : 'guest');
  }, [accessToken, setWorkflowRole]);

  // Defense-in-depth: `/embed/flow/:recipeId` is only meaningful when iframed by Figma.
  // CSP frame-ancestors blocks framing from non-Figma origins, but it doesn't block a
  // direct top-level navigation. Without this guard, `useAuthConfig` falls through to
  // FIREBASE (because `isInsideIframe()` is false), `accessToken` becomes a Firebase
  // token that `decodeFigmaEmbedToken` can't parse, and the workflow role + Weavy auth
  // both end up in inconsistent states. Treat the route as non-existent and let the
  // top-level catch-all in Router.tsx (`<Route path="*" element={<Navigate to="/" />} />`)
  // bounce to the dashboard.
  if (!isInsideIframe()) {
    return <Navigate to="/" replace />;
  }

  if (!recipeId) {
    // Should be unreachable — React Router requires the :recipeId param to match this route.
    // Render a static error rather than redirecting: standalone signin is not valid in embed mode
    // (blocked by frame-ancestors CSP) and Figma owns user auth here.
    return (
      <FlexCenHorVer sx={{ width: '100vw', height: '100vh', background: color.Black100 }}>
        <Typography color="inherit">Missing workflow id.</Typography>
      </FlexCenHorVer>
    );
  }

  if (!accessToken) {
    return (
      <FlexCenHorVer sx={{ width: '100vw', height: '100vh', background: color.Black100 }}>
        <CircularProgress disableShrink color="inherit" />
      </FlexCenHorVer>
    );
  }

  return <Recipe user={currentUser} />;
}
